import { useUggRouting } from "@ugitgud/legos/context/routing";
import { OmitPolymorphic } from "@ugitgud/legos/ui/shared";
import { BaseButtonProps } from "@ugitgud/legos/ui/form/button/Button";
import React, { useCallback, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import path from "path-browserify";

export type UggLinkProps<T extends React.ElementType> = OmitPolymorphic<T, BaseButtonProps<"button">, "colorScheme"> & {
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
};

export function isFullyQualifiedURL(url: string) {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
}

export const UggLink = React.forwardRef(
  <T extends React.ElementType = "a">(props: UggLinkProps<T>, ref: React.ForwardedRef<React.ElementRef<T>>) => {
    const { as, toggled, icon, children, size = "md", className, onClick, href, ...rest } = props;
    const { push, basePath, path: asPath } = useUggRouting();
    const dataProp = useMemo(() => (typeof toggled !== "undefined" ? { "data-toggled": toggled } : {}), [toggled]);
    const mergedClasses = useMemo(() => twMerge(`${className}`), [size, className]);
    const fullyQualified = href && isFullyQualifiedURL(href);
    const parentPath = path.resolve(basePath || "", asPath.replace(basePath || "", ""), "..");
    const link = fullyQualified ? href : path.join(basePath || "", !href?.startsWith("/") ? parentPath : "", href || "");
    const finalProps = {
      ...dataProp,
      ...rest,
      children: icon || children,
      className: mergedClasses,
    };
    const { target } = rest;
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (onClick) {
          onClick();
        }
        if (href) {
          if (!target) {
            if (!fullyQualified) {
              push(href);
            } else {
              window.location.href = href;
            }
            e.preventDefault();
            return;
          } else {
            window.open(href, target);
            e.preventDefault();
          }
        }
      },
      [target, onClick, href, fullyQualified],
    );
    const Component = (as as React.ElementType) || ("a" as React.ElementType);
    return <Component onClick={handleClick} href={link} {...finalProps} ref={ref} />;
  },
);
